import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { postProfileImage } from '../../api/users';
import {
  postOrganizationCoverPic,
  postOrganizationProfileImage,
} from '../../api/organizations';
import { postStreamCoverPic, postStreamProfilePic } from '../../api/streams';
import { postChannelCoverPic, postChannelProfilePic } from '../../api/channels';

import { createUiUID } from './utils/createUiUID';

import { REQUEST } from '../../constants/request';
import { KEYWORD_UI_UID } from './constants/keywordUiUid';

export const uploadMeUserProfilePic = createAsyncThunk(
  'ui/uploadMeUserProfilePic',
  async (formData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postProfileImage(formData, {
        cancelToken: source.token,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (_, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[
          createUiUID({ keyword: KEYWORD_UI_UID.profilePicMeUser })
        ] === REQUEST.pending
      );
    },
  },
);
export const uploadOrgProfilePic = createAsyncThunk(
  'ui/uploadOrgProfilePic',
  async ({ orgId, formData }, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postOrganizationProfileImage(
        { orgId, formData },
        {
          cancelToken: source.token,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[createUiUID({ orgId: arg.orgId })] === REQUEST.pending
      );
    },
  },
);

export const uploadOrgCoverPic = createAsyncThunk(
  'ui/uploadOrgCoverPic',
  async ({ orgId, formData }, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postOrganizationCoverPic(
        { orgId, formData },
        {
          cancelToken: source.token,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[
          createUiUID({ keyword: KEYWORD_UI_UID.coverPic, orgId: arg.orgId })
        ] === REQUEST.pending
      );
    },
  },
);

// uploadStreamProfileImage
export const uploadStreamProfilePic = createAsyncThunk(
  'ui/uploadStreamProfilePic',
  async ({ orgId, streamId, formData }, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postStreamProfilePic(
        { orgId, streamId, formData },
        {
          cancelToken: source.token,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[
          createUiUID({ orgId: arg.orgId, streamId: arg.streamId })
        ] === REQUEST.pending
      );
    },
  },
);
export const uploadStreamCoverPic = createAsyncThunk(
  'ui/postStreamCoverPic',
  async ({ orgId, streamId, formData }, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postStreamCoverPic(
        { orgId, streamId, formData },
        {
          cancelToken: source.token,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[
          createUiUID({
            keyword: KEYWORD_UI_UID.coverPic,
            orgId: arg.orgId,
            streamId: arg.streamId,
          })
        ] === REQUEST.pending
      );
    },
  },
);

// uploadChannelProfileImage
export const uploadChannelProfilePic = createAsyncThunk(
  'ui/uploadChannelProfilePic',
  async (
    { orgId, streamId, channelId, formData },
    { signal, rejectWithValue },
  ) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postChannelProfilePic(
        { orgId, streamId, channelId, formData },
        {
          cancelToken: source.token,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[
          createUiUID({
            orgId: arg.orgId,
            streamId: arg.streamId,
            channelId: arg.channelId,
          })
        ] === REQUEST.pending
      );
    },
  },
);

export const uploadChannelCoverPic = createAsyncThunk(
  'ui/uploadChannelCoverPic',
  async (
    { orgId, streamId, channelId, formData },
    { signal, rejectWithValue },
  ) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await postChannelCoverPic(
        { orgId, streamId, channelId, formData },
        {
          cancelToken: source.token,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: (arg, { getState }) => {
      const { currentLoadings } = getState().ui;

      return !(
        currentLoadings[
          createUiUID({
            keyword: KEYWORD_UI_UID.coverPic,
            orgId: arg.orgId,
            streamId: arg.streamId,
            channelId: arg.channelId,
          })
        ] === REQUEST.pending
      );
    },
  },
);

const initialState = {
  pageTitle: '',
  currentRequestIds: {},
  currentLoadings: {},
  currentErrors: {},
  popupMessage: { textList: [], className: '' },
  isShownWallet: false,
  bulkSelection: {
    list: [],
    type: '',
  },
  pending: false,
};
export const UiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setPageTitle: (state, { payload }) => {
      state.pageTitle = payload;
    },
    setPopupMessage: (state, { payload }) => {
      state.popupMessage = payload;
    },
    setPending: (state, { payload }) => {
      state.pending = payload;
    },
    resetPopupMessage: state => {
      state.popupMessage = { textList: [], className: '' };
    },
    setTypeBulkSelection: (state, { payload }) => {
      state.bulkSelection.type = payload;
    },
    toggleBulkSelection: (state, { payload }) => {
      if (state.bulkSelection.list.includes(payload)) {
        state.bulkSelection.list = state.bulkSelection.list.filter(
          item => item !== payload,
        );
      } else {
        state.bulkSelection.list.push(payload);
      }
    },
    resetBulkSelection: state => {
      state.bulkSelection.list = [];
      state.bulkSelection.type = '';
    },
    setIsShownWallet: (state, { payload }) => {
      state.isShownWallet = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(uploadMeUserProfilePic.pending, (state, { meta }) => {
      const uid = createUiUID(KEYWORD_UI_UID.profilePicMeUser);
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadMeUserProfilePic.fulfilled, (state, { meta }) => {
      const uid = createUiUID(KEYWORD_UI_UID.profilePicMeUser);
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(uploadMeUserProfilePic.rejected, (state, { error }) => {
      const uid = createUiUID(KEYWORD_UI_UID.profilePicMeUser);
      state.currentLoadings[uid] = REQUEST.idle;
      state.currentRequestIds[uid] = undefined;
      state.currentErrors[uid] = error;
    });
    builder.addCase(uploadOrgProfilePic.pending, (state, { meta }) => {
      const uid = createUiUID({ orgId: meta.arg.orgId });
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadOrgProfilePic.fulfilled, (state, { meta }) => {
      const uid = createUiUID({ orgId: meta.arg.orgId });
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(
      uploadOrgProfilePic.rejected,
      (state, { meta, payload }) => {
        const uid = createUiUID({ orgId: meta.arg.orgId });
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
        state.currentErrors[uid] = payload;
      },
    );
    builder.addCase(uploadOrgCoverPic.pending, (state, { meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
      });
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadOrgCoverPic.fulfilled, (state, { meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
      });
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(uploadOrgCoverPic.rejected, (state, { payload, meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
      });
      state.currentLoadings[uid] = REQUEST.idle;
      state.currentRequestIds[uid] = undefined;
      state.currentErrors[uid] = payload;
    });
    builder.addCase(uploadStreamProfilePic.pending, (state, { meta }) => {
      const uid = createUiUID({
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
      });
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadStreamProfilePic.fulfilled, (state, { meta }) => {
      const uid = createUiUID({
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
      });
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(
      uploadStreamProfilePic.rejected,
      (state, { meta, payload }) => {
        const uid = createUiUID({
          orgId: meta.arg.orgId,
          streamId: meta.arg.streamId,
        });
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
        state.currentErrors[uid] = payload;
      },
    );
    builder.addCase(uploadStreamCoverPic.pending, (state, { meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
      });
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadStreamCoverPic.fulfilled, (state, { meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
      });
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(
      uploadStreamCoverPic.rejected,
      (state, { payload, meta }) => {
        const uid = createUiUID({
          keyword: KEYWORD_UI_UID.coverPic,
          orgId: meta.arg.orgId,
          streamId: meta.arg.streamId,
        });
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
        state.currentErrors[uid] = payload;
      },
    );
    builder.addCase(uploadChannelProfilePic.pending, (state, { meta }) => {
      const uid = createUiUID({
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
        channelId: meta.arg.channelId,
      });
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadChannelProfilePic.fulfilled, (state, { meta }) => {
      const uid = createUiUID({
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
      });
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(
      uploadChannelProfilePic.rejected,
      (state, { meta, payload }) => {
        const uid = createUiUID({
          orgId: meta.arg.orgId,
          streamId: meta.arg.streamId,
          channelId: meta.arg.channelId,
        });
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
        state.currentErrors[uid] = payload;
      },
    );
    builder.addCase(uploadChannelCoverPic.pending, (state, { meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
        channelId: meta.arg.channelId,
      });
      state.currentLoadings[uid] = REQUEST.pending;
      state.currentRequestIds[uid] = meta.requestId;
    });
    builder.addCase(uploadChannelCoverPic.fulfilled, (state, { meta }) => {
      const uid = createUiUID({
        keyword: KEYWORD_UI_UID.coverPic,
        orgId: meta.arg.orgId,
        streamId: meta.arg.streamId,
        channelId: meta.arg.channelId,
      });
      if (
        state.currentLoadings[uid] === REQUEST.pending &&
        state.currentRequestIds[uid] === meta.requestId
      ) {
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
      }
    });
    builder.addCase(
      uploadChannelCoverPic.rejected,
      (state, { payload, meta }) => {
        const uid = createUiUID({
          keyword: KEYWORD_UI_UID.coverPic,
          orgId: meta.arg.orgId,
          streamId: meta.arg.streamId,
          channelId: meta.arg.channelId,
        });
        state.currentLoadings[uid] = REQUEST.idle;
        state.currentRequestIds[uid] = undefined;
        state.currentErrors[uid] = payload;
      },
    );
  },
});

export const {
  resetPopupMessage,
  setPopupMessage,
  setPending,
  setPageTitle,
  setTypeBulkSelection,
  toggleBulkSelection,
  resetBulkSelection,
  setIsShownWallet,
} = UiSlice.actions;

export default UiSlice.reducer;
